/**
 * Predictive Viral Scoring Engine
 * ---------------------------------------------------------------
 * Forecasts behavioral performance for newly generated assets
 * BEFORE render/export. Pure functions + a persistence helper.
 *
 * Inputs:  asset features (archetype, format, escalation, pacing,
 *          platform, intensity, hook) + active intelligence biases
 *          + recent learning memory (rolling_score per dimension).
 *
 * Outputs: replay / completion / save / export probabilities,
 *          composite virality, platform fit, hook efficiency,
 *          dominant signal label, confidence.
 */

import { supabase } from "@/integrations/supabase/client";
import type { ActiveBias } from "@/lib/adaptiveBiases";

// ──────────────────────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────────────────────

export interface PredictionFeatures {
  assetId: string;
  archetype: string;
  format: string;            // replay | spiral | pov | ...
  escalationPath?: string;   // emotional | defensive | guilt | withdrawal | ...
  pacing?: string;           // low | medium | high
  platform: string;          // tiktok | instagram | youtube | internal | multi
  hook?: string | null;
}

export interface AssetPrediction {
  assetId: string;
  predictedReplayRate: number;       // 0–100
  predictedCompletionRate: number;   // 0–100
  predictedSaveRate: number;         // 0–100  (saves per 100 views)
  predictedExportRate: number;       // 0–100
  predictedEngagementScore: number;  // 0–100
  predictedViralityScore: number;    // 0–100
  predictedPlatformFit: number;      // 0–100
  hookEfficiency: number;            // 0–100
  emotionalEscalationStrength: number; // 0–100
  dominantSignal: string;            // "DEFLECTION + POV"
  confidence: number;                // 0–100
}

interface ProfileMemory {
  dimension: string;
  key: string;
  rolling_score: number; // 0–100
  trend_delta: number;   // -100..+100
  weight: number;
  sample_size: number;
}

// ──────────────────────────────────────────────────────────────
// Static heuristics — neutral baselines
// ──────────────────────────────────────────────────────────────

const PLATFORM_FORMAT_FIT: Record<string, Record<string, number>> = {
  tiktok:    { replay: 86, spiral: 78, pov: 74 },
  instagram: { replay: 72, spiral: 68, pov: 80 },
  youtube:   { replay: 58, spiral: 70, pov: 76 },
};

const ESCALATION_REPLAY_BASE: Record<string, number> = {
  withdrawal: 78, guilt: 72, defensive: 66, emotional: 60, shame: 70,
};

const ESCALATION_COMPLETION_BASE: Record<string, number> = {
  withdrawal: 64, guilt: 70, defensive: 68, emotional: 58, shame: 66,
};

const FORMAT_COMPLETION_BASE: Record<string, number> = {
  replay: 68, spiral: 60, pov: 72,
};

const FORMAT_SAVE_BASE: Record<string, number> = {
  pov: 18, replay: 12, spiral: 9,
};

const PACING_FACTOR: Record<string, number> = {
  low: 0.92, medium: 1.0, high: 1.08,
};

// Hook patterns we can detect from the phrase itself
function classifyHook(hook: string): { label: string; lift: number } {
  const h = hook.trim().toLowerCase();
  if (/^(why|how|what|imagine)\b/.test(h)) return { label: "CURIOSITY", lift: 8 };
  if (/^(if you|when (he|she|they)|the moment)/.test(h)) return { label: "REPLAY TRIGGER", lift: 12 };
  if (/^(stop|don't|never|warning)/.test(h)) return { label: "PATTERN INTERRUPT", lift: 10 };
  if (/^(i used to|i thought|i didn't)/.test(h)) return { label: "POV ANCHOR", lift: 9 };
  if (h.endsWith("?")) return { label: "OPEN LOOP", lift: 6 };
  return { label: "STATEMENT", lift: 0 };
}

// ──────────────────────────────────────────────────────────────
// Memory loaders
// ──────────────────────────────────────────────────────────────

export async function loadActiveBiases(): Promise<ActiveBias[]> {
  const { data } = await (supabase as any)
    .from("generation_biases")
    .select("id, dimension, key, bias_direction, weight, confidence, impact_score, rationale, cycle_id")
    .eq("active", true);
  return (data ?? []) as ActiveBias[];
}

export async function loadLearningMemory(): Promise<ProfileMemory[]> {
  const { data } = await (supabase as any)
    .from("adaptive_learning_profiles")
    .select("dimension, key, rolling_score, trend_delta, weight, sample_size");
  return (data ?? []) as ProfileMemory[];
}

// Lookup helpers
function memScore(mem: ProfileMemory[], dim: string, key: string | undefined | null): {
  score: number; trend: number; sample: number;
} {
  if (!key) return { score: 0, trend: 0, sample: 0 };
  const row = mem.find((r) => r.dimension === dim && r.key === key);
  if (!row) return { score: 0, trend: 0, sample: 0 };
  return { score: row.rolling_score, trend: row.trend_delta, sample: row.sample_size };
}

function biasShift(biases: ActiveBias[], dim: string, key: string | undefined | null): number {
  if (!key) return 0;
  const b = biases.find((x) => x.dimension === dim && x.key === key);
  if (!b) return 0;
  const magnitude = (b.confidence / 100) * (b.weight || 1) * 14; // up to ±14
  return b.bias_direction === "increase" ? magnitude : -magnitude;
}

// ──────────────────────────────────────────────────────────────
// Core scorer
// ──────────────────────────────────────────────────────────────

export function predictAsset(
  f: PredictionFeatures,
  biases: ActiveBias[],
  mem: ProfileMemory[]
): AssetPrediction {
  const platform = (f.platform || "multi").toLowerCase();
  const format = (f.format || "replay").toLowerCase();
  const escalation = (f.escalationPath || "emotional").toLowerCase();
  const pacing = (f.pacing || "medium").toLowerCase();

  // Platform fit
  const fitTable = PLATFORM_FORMAT_FIT[platform] ?? { replay: 70, spiral: 70, pov: 70 };
  let platformFit = fitTable[format] ?? 65;
  platformFit += biasShift(biases, "platform", platform) * 0.5;
  platformFit = clamp(platformFit + memScore(mem, "platform", platform).score * 0.1, 10, 100);

  // Replay rate
  let replay =
    (ESCALATION_REPLAY_BASE[escalation] ?? 60) * 0.55 +
    (format === "replay" ? 22 : format === "spiral" ? 14 : 8) +
    (PACING_FACTOR[pacing] - 1) * 35;
  replay += biasShift(biases, "escalation", escalation) * 0.7;
  replay += biasShift(biases, "format", format) * 0.5;
  replay += memScore(mem, "format", format).trend * 0.15;
  replay = clamp(replay, 5, 99);

  // Completion rate
  let completion =
    (FORMAT_COMPLETION_BASE[format] ?? 60) * 0.6 +
    (ESCALATION_COMPLETION_BASE[escalation] ?? 60) * 0.3 +
    (PACING_FACTOR[pacing] - 1) * -20; // higher pacing → slightly lower completion
  completion += biasShift(biases, "format", format) * 0.4;
  completion += memScore(mem, "archetype", f.archetype).score * 0.05;
  completion = clamp(completion, 8, 98);

  // Save rate (per 100 views, so capped to ~30)
  let save = FORMAT_SAVE_BASE[format] ?? 10;
  save += escalation === "guilt" || escalation === "shame" ? 4 : 0;
  save += biasShift(biases, "format", format) * 0.15;
  save += memScore(mem, "archetype", f.archetype).trend * 0.06;
  save = clamp(save, 1, 32);

  // Export rate (per 100 views)
  let exportRate = (save * 0.6) + (format === "pov" ? 4 : 2);
  exportRate += biasShift(biases, "archetype", f.archetype) * 0.08;
  exportRate = clamp(exportRate, 0.5, 28);

  // Hook efficiency
  const hookCls = f.hook ? classifyHook(f.hook) : { label: "STATEMENT", lift: 0 };
  const hookEff = clamp(50 + hookCls.lift * 3 + (replay - 60) * 0.4, 5, 99);

  // Emotional escalation strength
  const escStrength = clamp(
    (ESCALATION_REPLAY_BASE[escalation] ?? 60) * 0.5 +
    (PACING_FACTOR[pacing] - 1) * 60 +
    biasShift(biases, "escalation", escalation),
    5, 99
  );

  // Engagement composite
  const engagement = clamp(
    replay * 0.30 +
    completion * 0.25 +
    save * 1.4 +
    exportRate * 1.2 +
    hookEff * 0.20,
    5, 99
  );

  // Virality composite — leans on replay × platform fit × hook
  const virality = clamp(
    replay * 0.32 +
    platformFit * 0.22 +
    hookEff * 0.18 +
    save * 1.0 +
    exportRate * 0.8 +
    completion * 0.10,
    5, 99
  );

  // Dominant signal label
  const tactics: { tag: string; weight: number }[] = [
    { tag: "DEFLECTION", weight: escalation === "defensive" ? 1 : 0 },
    { tag: "GUILT",      weight: escalation === "guilt" ? 1 : 0 },
    { tag: "WITHDRAWAL", weight: escalation === "withdrawal" ? 1 : 0 },
    { tag: "SHAME",      weight: escalation === "shame" ? 1 : 0 },
    { tag: "POV",        weight: format === "pov" ? 1 : 0 },
    { tag: "REPLAY",     weight: format === "replay" ? 1 : 0 },
    { tag: "SPIRAL",     weight: format === "spiral" ? 1 : 0 },
  ].filter((t) => t.weight > 0);
  const dominant = tactics.length
    ? tactics.slice(0, 2).map((t) => t.tag).join(" + ")
    : f.archetype.toUpperCase();

  // Confidence — increases with sample sizes and active biases
  const samplesSeen =
    memScore(mem, "format", format).sample +
    memScore(mem, "archetype", f.archetype).sample +
    memScore(mem, "platform", platform).sample;
  const confidence = clamp(
    40 +
    Math.min(35, samplesSeen / 8) +
    Math.min(15, biases.length * 2) +
    (Math.abs(biasShift(biases, "format", format)) > 0 ? 5 : 0),
    25, 99
  );

  return {
    assetId: f.assetId,
    predictedReplayRate: round(replay),
    predictedCompletionRate: round(completion),
    predictedSaveRate: round1(save),
    predictedExportRate: round1(exportRate),
    predictedEngagementScore: round(engagement),
    predictedViralityScore: round(virality),
    predictedPlatformFit: round(platformFit),
    hookEfficiency: round(hookEff),
    emotionalEscalationStrength: round(escStrength),
    dominantSignal: dominant,
    confidence: round(confidence),
  };
}

// ──────────────────────────────────────────────────────────────
// Persist predictions for a batch (upsert by asset_id)
// ──────────────────────────────────────────────────────────────

export async function persistPredictions(preds: AssetPrediction[]): Promise<void> {
  if (preds.length === 0) return;
  const rows = preds.map((p) => ({
    asset_id: p.assetId,
    predicted_replay_rate: p.predictedReplayRate,
    predicted_completion_rate: p.predictedCompletionRate,
    predicted_save_rate: p.predictedSaveRate,
    predicted_export_rate: p.predictedExportRate,
    predicted_engagement_score: p.predictedEngagementScore,
    predicted_virality_score: p.predictedViralityScore,
    predicted_platform_fit: p.predictedPlatformFit,
    hook_efficiency: p.hookEfficiency,
    emotional_escalation_strength: p.emotionalEscalationStrength,
    dominant_signal: p.dominantSignal,
    confidence: p.confidence,
  }));
  const { error } = await (supabase as any)
    .from("asset_predictions")
    .upsert(rows, { onConflict: "asset_id" });
  if (error) console.error("[predictionEngine] persist failed", error);
}

// One-shot helper used after every batch generation.
export async function scoreAndPersist(
  features: PredictionFeatures[]
): Promise<AssetPrediction[]> {
  if (features.length === 0) return [];
  const [biases, mem] = await Promise.all([loadActiveBiases(), loadLearningMemory()]);
  const preds = features.map((f) => predictAsset(f, biases, mem));
  await persistPredictions(preds);
  // Broadcast so dashboards refresh.
  window.dispatchEvent(new CustomEvent("asset-predictions-updated", {
    detail: { count: preds.length },
  }));
  return preds;
}

// ──────────────────────────────────────────────────────────────
// Utils
// ──────────────────────────────────────────────────────────────

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}
function round(n: number) { return Math.round(n); }
function round1(n: number) { return Math.round(n * 10) / 10; }

export const ASSET_PREDICTIONS_UPDATED_EVENT = "asset-predictions-updated";
